import React, { useState, useRef } from 'react'
import { Formik, Form, Field } from 'formik'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

import { db } from '../../FirebaseInit'
import { useToast } from '../../context/ToastContext'
import {
  buildTicketValidationSchema,
  URGENCIES,
  ENVIRONMENTS,
  availableProducts,
  buildTicketRecord
} from '../../utils/tickets/ticket-logic'
import AttachmentUploader from './AttachmentUploader'

// TicketForm (design: Pages and UI components; Req 1.1, 1.2, 1.3, 1.4, 1.5)
//
// Formik-driven ticket submission form. Description and urgency are validated by
// the shared `ticketValidationSchema`, with field-level messages surfaced for an
// empty/whitespace description (Req 1.3) and a missing urgency (Req 1.4). The
// urgency control is a select restricted to URGENCIES (Req 1.5). Attachments are
// managed in local state via AttachmentUploader; on submit the record is built
// with `buildTicketRecord` and its client Date timestamps are replaced with
// Firestore serverTimestamp() before writing to the `tickets` collection
// (Req 1.1, 1.2).
const TicketForm = ({ customerId, customerName, authorEmail, isMediproCustomer, isAcaproCustomer, onSubmitted }) => {
  const { showToast } = useToast()
  const [attachments, setAttachments] = useState([])

  // Product options come from the customer's feature-access flags. When only
  // one product is available it is preselected and the field is not required.
  const productOptions = availableProducts({ isMediproCustomer, isAcaproCustomer })
  const validationSchema = buildTicketValidationSchema(productOptions)
  const initialProduct = productOptions.length === 1 ? productOptions[0] : ''

  // A stable draft id groups this submission's uploads before the ticket exists.
  const draftIdRef = useRef(crypto.randomUUID())

  // Holds the AttachmentUploader's paste handler so the description textarea can
  // route paste events into it (pasted images become attachments; Req 2.2).
  const pasteHandlerRef = useRef(null)

  const handleDescriptionPaste = (event) => {
    if (typeof pasteHandlerRef.current === 'function') {
      pasteHandlerRef.current(event)
    }
  }

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const record = buildTicketRecord({
        customerId,
        customerName,
        authorEmail,
        description: values.description,
        urgency: values.urgency,
        environments: values.environments,
        product: values.product,
        attachments
      })

      // Replace client Date timestamps with authoritative server timestamps.
      const docRef = await addDoc(collection(db, 'tickets'), {
        ...record,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        statusChangedAt: serverTimestamp()
      })

      showToast('Ticket submitted', 'green')
      resetForm()
      setAttachments([])
      draftIdRef.current = crypto.randomUUID()

      if (typeof onSubmitted === 'function') onSubmitted(docRef.id)
    } catch (err) {
      console.error('Failed to submit ticket:', err)
      showToast('Failed to submit ticket', 'red')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={{ description: '', urgency: '', environments: [], product: initialProduct }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form className='space-y-5 text-left'>
          <div>
            <label htmlFor='description' className='block text-sm font-semibold text-gray-800'>
              Description
            </label>
            <p className='mb-1 text-xs text-gray-500'>Describe the issue in as much detail as you can.</p>
            <Field
              as='textarea'
              id='description'
              name='description'
              rows={6}
              placeholder='Describe your issue... (you can paste a screenshot here)'
              onPaste={handleDescriptionPaste}
              className='block w-full rounded-md border border-gray-300 p-3 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none'
            />
            {touched.description && errors.description && (
              <p className='mt-1 text-sm text-red-600'>{errors.description}</p>
            )}
          </div>

          <div>
            <label htmlFor='urgency' className='block text-sm font-semibold text-gray-800'>
              Urgency
            </label>
            <p className='mb-1 text-xs text-gray-500'>How urgently do you need this resolved?</p>
            <Field
              as='select'
              id='urgency'
              name='urgency'
              className='block w-full max-w-xs rounded-md border border-gray-300 p-2.5 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none'
            >
              <option value=''>Select urgency...</option>
              {URGENCIES.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </Field>
            {touched.urgency && errors.urgency && (
              <p className='mt-1 text-sm text-red-600'>{errors.urgency}</p>
            )}
          </div>

          <div>
            <span className='block text-sm font-semibold text-gray-800'>Environment</span>
            <p className='mb-1 text-xs text-gray-500'>Which environment(s) does this affect?</p>
            <div className='flex flex-wrap gap-4'>
              {ENVIRONMENTS.map((env) => (
                <label key={env} className='flex items-center gap-2 text-sm text-gray-700'>
                  <Field type='checkbox' name='environments' value={env} className='h-4 w-4' />
                  {env}
                </label>
              ))}
            </div>
            {touched.environments && errors.environments && (
              <p className='mt-1 text-sm text-red-600'>{errors.environments}</p>
            )}
          </div>

          {productOptions.length > 0 && (
            <div>
              <span className='block text-sm font-semibold text-gray-800'>Product</span>
              <p className='mb-1 text-xs text-gray-500'>Which product is this about?</p>
              {productOptions.length === 1
                ? (
                  <p className='text-sm text-gray-700'>{productOptions[0]}</p>
                  )
                : (
                  <div className='flex flex-wrap gap-4'>
                    {productOptions.map((prod) => (
                      <label key={prod} className='flex items-center gap-2 text-sm text-gray-700'>
                        <Field type='radio' name='product' value={prod} className='h-4 w-4' />
                        {prod}
                      </label>
                    ))}
                  </div>
                  )}
              {touched.product && errors.product && (
                <p className='mt-1 text-sm text-red-600'>{errors.product}</p>
              )}
            </div>
          )}

          <div>
            <span className='block text-sm font-semibold text-gray-800'>Attachments</span>
            <p className='mb-1 text-xs text-gray-500'>Optional. Add screenshots or files, or paste an image into the description.</p>
            <AttachmentUploader
              customerId={customerId}
              draftId={draftIdRef.current}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
              registerPasteHandler={(getHandler) => { pasteHandlerRef.current = getHandler() }}
            />
          </div>

          <div className='flex justify-end border-t border-gray-100 pt-4'>
            <button
              type='submit'
              disabled={isSubmitting}
              className='rounded-md bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300'
            >
              {isSubmitting ? 'Submitting...' : 'Submit ticket'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default TicketForm
